import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestAdminPanel = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [testForm, setTestForm] = useState({
    title: '',
    subject: 'Mathematics',
    grade: 'Class 1',
    difficulty: 'easy',
    duration_minutes: 15,
    questions: [{ question: '', options: ['', '', '', ''], correct_answer: '' }]
  });

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    if (!['test_admin', 'admin'].includes(user.role)) {
      toast.error('Access denied: Test Admin only');
      navigate('/dashboard');
      return;
    }
    fetchTests();
  }, []);

  const fetchTests = async () => {
    try {
      const response = await axios.get(`${API}/tests`);
      setTests(response.data.tests || []);
    } catch (error) {
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTest = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tests`, testForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Test created successfully!');
      setShowCreate(false);
      setTestForm({
        title: '',
        subject: 'Mathematics',
        grade: 'Class 1',
        difficulty: 'easy',
        duration_minutes: 15,
        questions: [{ question: '', options: ['', '', '', ''], correct_answer: '' }]
      });
      fetchTests();
    } catch (error) {
      toast.error('Failed to create test');
    }
  };

  const addQuestion = () => {
    setTestForm({
      ...testForm,
      questions: [...testForm.questions, { question: '', options: ['', '', '', ''], correct_answer: '' }]
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12">
      <div className="max-w-7xl mx-auto">
        <Button data-testid="back-btn" variant="ghost" onClick={() => { localStorage.clear(); navigate('/'); }} className="mb-6 rounded-full">
          <ArrowLeft className="mr-2 h-4 w-4" /> Logout
        </Button>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold mb-2">Test Management</h1>
            <p className="text-muted-foreground">Create and manage tests</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="rounded-full">
                <Plus className="mr-2 h-4 w-4" /> Create Test
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create New Test</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateTest} className="space-y-4">
                <div>
                  <Label>Test Title</Label>
                  <Input value={testForm.title} onChange={(e) => setTestForm({ ...testForm, title: e.target.value })} required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Subject</Label>
                    <Select value={testForm.subject} onValueChange={(value) => setTestForm({ ...testForm, subject: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['English', 'Mathematics', 'Science', 'Phonics and Early Literacy', 'Reasoning and Logical Thinking'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Grade</Label>
                    <Select value={testForm.grade} onValueChange={(value) => setTestForm({ ...testForm, grade: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Difficulty</Label>
                    <Select value={testForm.difficulty} onValueChange={(value) => setTestForm({ ...testForm, difficulty: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['easy', 'medium', 'hard'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input type="number" value={testForm.duration_minutes} onChange={(e) => setTestForm({ ...testForm, duration_minutes: parseInt(e.target.value) })} required />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <Label className="text-lg">Questions</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addQuestion}>
                      <Plus className="h-4 w-4 mr-1" /> Add Question
                    </Button>
                  </div>
                  {testForm.questions.map((q, idx) => (
                    <div key={idx} className="p-4 border rounded-lg mb-3">
                      <Label className="text-sm mb-2">Question {idx + 1}</Label>
                      <Input placeholder="Question text" value={q.question} onChange={(e) => {
                        const newQuestions = [...testForm.questions];
                        newQuestions[idx].question = e.target.value;
                        setTestForm({ ...testForm, questions: newQuestions });
                      }} className="mb-2" required />
                      {q.options.map((opt, optIdx) => (
                        <Input key={optIdx} placeholder={`Option ${optIdx + 1}`} value={opt} onChange={(e) => {
                          const newQuestions = [...testForm.questions];
                          newQuestions[idx].options[optIdx] = e.target.value;
                          setTestForm({ ...testForm, questions: newQuestions });
                        }} className="mb-2" required />
                      ))}
                      <Input placeholder="Correct Answer" value={q.correct_answer} onChange={(e) => {
                        const newQuestions = [...testForm.questions];
                        newQuestions[idx].correct_answer = e.target.value;
                        setTestForm({ ...testForm, questions: newQuestions });
                      }} required />
                    </div>
                  ))}
                </div>

                <Button type="submit" className="w-full rounded-full">Create Test</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4">
          {tests.map((test) => (
            <div key={test.test_id} className="card-base p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-xl mb-2">{test.title}</h3>
                  <div className="flex gap-2 flex-wrap">
                    <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">{test.subject}</span>
                    <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-semibold">{test.grade}</span>
                    <span className="px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold">{test.difficulty}</span>
                    <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-semibold">{test.duration_minutes} min</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full"><Edit className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" className="rounded-full text-destructive"><Trash className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestAdminPanel;